import React, { useEffect, useRef, useState } from "react";
import { motion, useInView, useMotionValue, animate } from "framer-motion";

export const Container = ({ className = "", children }) => (
  <div className={`max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 ${className}`}>{children}</div>
);

export const Reveal = ({ children, delay = 0, y = 28, className = "", once = true }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once, margin: "-80px" }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

export const Overline = ({ children, className = "", light = false }) => (
  <span
    className={`overline inline-flex items-center gap-2 ${
      light ? "text-teal-100" : "text-teal-700"
    } ${className}`}
    data-testid="overline-label"
  >
    <span className={`h-px w-6 ${light ? "bg-coral" : "bg-coral"}`} />
    {children}
  </span>
);

export const SectionHeading = ({ overline, title, subtitle, light = false, center = false }) => (
  <div className={`${center ? "text-center mx-auto max-w-2xl" : "max-w-2xl"} mb-12`}>
    {overline && <Overline light={light}>{overline}</Overline>}
    <h2
      className={`font-display text-3xl sm:text-4xl lg:text-[2.75rem] font-extrabold tracking-tight mt-4 leading-[1.1] text-balance ${
        light ? "text-white" : "text-navy"
      }`}
    >
      {title}
    </h2>
    {subtitle && (
      <p className={`mt-4 text-base sm:text-lg leading-relaxed ${light ? "text-teal-100/90" : "text-slate-600"}`}>
        {subtitle}
      </p>
    )}
  </div>
);

export const Counter = ({ value, suffix = "", duration = 2 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const mv = useMotionValue(0);
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(mv, value, {
      duration,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return controls.stop;
  }, [inView, value, duration, mv]);

  return (
    <span ref={ref}>
      {display.toLocaleString("en-IN")}
      {suffix}
    </span>
  );
};
